import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link2, Unlink } from "lucide-react";

const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ProjectRevenue({ pid, canManage }) {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("");
  const load = useCallback(() => { api.get(`/projects/${pid}/revenue`).then((r) => setD(r.data)).catch(() => setD({ quotes: [], options: [] })); }, [pid]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); setPick(""); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } };
  if (!d) { return <p className="text-sm text-slate-400">Loading…</p>; }
  const accepted = d.quotes.filter((q) => ["accepted", "approved"].includes((q.status || "").toLowerCase()));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="project-revenue">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-bold text-slate-900 mr-auto">Quotes linked to this project</h3>
        <span className="text-xs text-slate-500" data-testid="project-revenue-contracted">Accepted, not invoiced: <b>{gbp(accepted.filter((q) => !q.invoiced).reduce((s, q) => s + q.net, 0))}</b> ex VAT</span>
      </div>
      {canManage && (
        <div className="flex gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-9 flex-1 rounded-lg border border-slate-200 px-3 text-sm" data-testid="project-quote-pick">
            <option value="">— Link a quote —</option>
            {d.options.map((o) => <option key={o.quote_number} value={o.quote_number}>{o.quote_number} · {o.customer || ""} · {o.status}</option>)}
          </select>
          <Button size="sm" disabled={!pick} onClick={() => act(() => api.put(`/projects/${pid}/quotes/${pick}`), "Quote linked")} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="project-quote-link-btn"><Link2 className="w-4 h-4" /> Link</Button>
        </div>)}
      <div className="divide-y divide-slate-100 text-sm">
        {d.quotes.map((q) => (
          <div key={q.quote_number} className="flex items-center gap-3 py-2" data-testid={`project-quote-${q.quote_number}`}>
            <Link to={`/quotes/${q.quote_number}`} className="font-mono font-semibold text-indigo-600">{q.quote_number}</Link>
            <span className="flex-1 truncate text-slate-600">{q.customer} · {q.status}{q.invoiced ? " · invoiced" : ""} · {q.link === "direct" ? "linked" : `via job ${q.task_legacy_id}`}</span>
            <span className="tabular-nums">{gbp(q.net)} <span className="text-xs text-slate-400">ex VAT</span></span>
            {q.deposit_requested > 0 && <span className="text-xs text-slate-500">deposit {gbp(q.deposit_requested)}</span>}
            {canManage && q.link === "direct" && <button onClick={() => act(() => api.delete(`/projects/${pid}/quotes/${q.quote_number}`), "Quote unlinked")} className="text-slate-300 hover:text-rose-600" title="Unlink" data-testid={`project-quote-unlink-${q.quote_number}`}><Unlink className="w-4 h-4" /></button>}
          </div>))}
        {!d.quotes.length && <p className="py-6 text-center text-slate-400" data-testid="project-revenue-empty">No quotes linked yet. Quotes on this project's jobs appear here automatically.</p>}
      </div>
      <p className="text-xs text-slate-400">Profitability (income, contracted value, cash received, costs, margin) is in Finance → Reports → Project Profitability.</p>
    </div>
  );
}
